function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const {
  Offer,
  SimpleFlow
} = require('../entities');

function _async(f) {
  return function () {
    for (var args = [], i = 0; i < arguments.length; i++) {
      args[i] = arguments[i];
    }

    try {
      return Promise.resolve(f.apply(this, args));
    } catch (e) {
      return Promise.reject(e);
    }
  };
}

const AccessContext = require('./contexts/access-context');

const OfferRegionsRepo = require('./offer-regions-repo');

const OfferFlowsRepo = require('./offer-flows-repo');

const Schedule = require('../entities/schedule');

const getOfferDetails = (context
/*: AccessContext*/
, client
/*: ApiClient*/
, parse
/*: Object => Offer*/
, fields
/*: Array<string>*/
, offerId
/*: string*/
) =>
/*: Promise<Offer>*/
{
  const {
    propertyId,
    businessId
  } = context.serialize();
  const path = `/properties/${propertyId}/businesses/${businessId}/offers/${offerId}`;
  const req = new client.Request(path, {}, fields);
  req.parse = parse;
  return client.get(req);
};

const getOfferSchedulesStep = _async(function (context
/*: AccessContext*/
, client
/*: ApiClient*/
, offer
/*: Offer*/
)
/*: Promise<Offer>*/
{
  const offerId = offer.id;
  if (!offerId) throw new Error('Missing required parameter offer.id');
  const scheduleIds = offer.scheduleIds;

  if (!scheduleIds || !scheduleIds.length) {
    /* eslint-disable-next-line no-param-reassign */
    offer.schedules = [];
    return offer;
  }

  return _await(Promise.all(scheduleIds.map(id => client.schedules.for(id).details())), function (schedules) {
    // HACK: Set partialDiscoverableSchedule on usableSchedule based on discoverableSchedule
    const discoverable = schedules.find(s => s.type === Schedule.TYPE_NAMES.discoverable);
    const usable = schedules.find(s => s.type === Schedule.TYPE_NAMES.usable);

    if (usable) {
      usable.partialDiscoverableSchedule = discoverable && !(discoverable.allWeekDays && discoverable.allTimes);
    } // Relates to Portal > Edit Offers > Advance > Hidden Toggle


    schedules.forEach(schedule => {
      // .forEach instead of .map when iterating for side-effects
      schedule.isHidden = !discoverable; // eslint-disable-line no-param-reassign

      return schedule;
    });
    offer.schedules = schedules; // eslint-disable-line no-param-reassign

    return offer;
  });
}); // TODO: Move to offer-locations-repo


const getOfferLocationsStep = (context
/*: AccessContext*/
, client
/*: ApiClient*/
, offer
/*: Offer*/
) =>
/*: Promise<Offer>*/
{
  const {
    propertyId,
    businessId
  } = context.serialize();
  const offerId = offer.id;
  if (!offerId) throw new Error('Missing required parameter offer.id');
  const path = `/properties/${propertyId}/businesses/${businessId}/offers/${offerId}/locations`;
  const req = new client.Request(path, {}, ['id']);
  return client.get(req).then(client.pagination.loadRest).then(locations => {
    const locationIds = locations.items.map(loc => loc.id);
    offer.locations = locations.items;
    /* eslint-disable-line no-param-reassign */

    offer.locationIds = locationIds;
    /* eslint-disable-line no-param-reassign */

    return offer;
  });
};

const getOfferRegions = (fetchRegionIds
/*: () => Array<string>*/
, client) =>
/*: Array<Region>*/
fetchRegionIds().then(ids => ids.items.map(item => item.id)).then(ids => client.regions.multiDetails(ids)).then(regions => regions.items);

const getOfferRegionsStep = (context
/*: AccessContext*/
, client
/*: ApiClient*/
, offer
/*: Offer*/
) =>
/*: Promise<Offer>*/
{
  const offerId = offer.id;
  if (!offerId) throw new Error('Missing required parameter offer.id');
  const offerRegions = new OfferRegionsRepo(client, context, offerId);
  return Promise.all([getOfferRegions(() => offerRegions.list({
    discoverable: true
  }), client)]).then(([discoverable, redeemable]) => {
    /* eslint-disable-next-line no-param-reassign */
    offer.discoverableRegions = discoverable;
    return offer;
  });
};

const getOfferFlowStep = (context
/*: AccessContext*/
, client
/*: ApiClient*/
, offer
/*: Offer*/
) =>
/*: Promise<Offer> | Offer*/
{
  const offerId = offer.id;
  const flowId = offer.flowId;
  if (!offerId) throw new Error('Missing required parameter offer.id');
  return Promise.resolve().then(() => {
    // Here we try to 'heal' Offers that don't have a Flow...
    if (!flowId) return client.flows.initialize('default');
    const offerFlows = new OfferFlowsRepo(client, context, offerId);
    return offerFlows.details(flowId);
  }).then(flow => {
    /* eslint-disable-next-line no-param-reassign */
    offer.flow = SimpleFlow.fromFlow(flow);
    return offer;
  });
};

const getOfferCodesStep = _async(function (context
/*: AccessContext*/
, client
/*: ApiClient*/
, offer
/*: Offer*/
)
/*: Promise<Object>*/
{
  const {
    propertyId,
    businessId
  } = context.serialize();
  const offerId = offer.id;
  return _await(client.access.checkPermissions({
    propertyId,
    businessId
  }, ['property.business.offer.pins.get']), function (hasPermissions) {
    return hasPermissions ? _await(client.properties.for(propertyId).businesses.for(businessId).offers.for(offerId).codes.getPins(), function (pins) {
      /* eslint-disable-next-line no-param-reassign */
      offer.pins = pins.pins;
      return offer;
    }) : offer;
  });
});

const offerDetailsPipeline = (client
/*: ApiClient*/
, context
/*: AccessContext*/
, parse
/*: Object => Offer*/
, fields
/*: Array<string>*/
, offerId
/*: string*/
) => getOfferDetails(context, client, parse, fields, offerId).then(offer => getOfferSchedulesStep(context, client, offer)).then(offer => getOfferLocationsStep(context, client, offer)).then(offer => getOfferRegionsStep(context, client, offer)).then(offer => getOfferFlowStep(context, client, offer)).then(offer => getOfferCodesStep(context, client, offer));

module.exports = offerDetailsPipeline;